import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import Input, { Select } from "../../components/system/Input";
import EmptyState from "../../components/system/EmptyState";
import StatusChip from "../../components/system/StatusChip";
import {
  OperatorTable,
  OperatorTableRow,
} from "../../components/system/OperatorTable";

/**
 * OperatorRemovalsPage — `/platform/companies/:cid/removals`
 *
 *   ?days=7|30|90|365 (default 30)
 *   ?store=<storeId>
 *   ?department=<deptId>
 *   ?q=<freetext>
 *
 * Phase 1: READ-ONLY.
 */
const REASON_TONE = {
  expired: "expired",
  near_expiration: "urgent",
  damaged: "soon",
  sold_out: "fresh",
  other: "neutral",
};

export default function OperatorRemovalsPage() {
  const { cid } = useParams();
  const [params, setParams] = useSearchParams();
  const [companyName, setCompanyName] = useState("");
  const [removals, setRemovals] = useState([]);
  const [stores, setStores] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  const days = parseInt(params.get("days") || "30", 10);
  const storeId = params.get("store") || "";
  const deptId = params.get("department") || "";
  const reason = params.get("reason") || "";
  const q = params.get("q") || "";

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = async () => {
    setLoading(true);
    try {
      const parts = [`days=${days}`, "limit=500"];
      if (storeId) parts.push(`store_id=${encodeURIComponent(storeId)}`);
      if (deptId) parts.push(`department_id=${encodeURIComponent(deptId)}`);
      const qs = parts.join("&");
      const [det, r, st, dp] = await Promise.all([
        api.get(`/platform/companies/${cid}`),
        api.get(`/platform/companies/${cid}/removals?${qs}`),
        api.get(`/platform/companies/${cid}/stores`),
        api.get(`/platform/companies/${cid}/departments`),
      ]);
      setCompanyName(det.data.company?.name || "");
      setRemovals(r.data.removals || []);
      setStores(st.data.stores || []);
      setDepartments(dp.data.departments || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [cid, days, storeId, deptId]); // eslint-disable-line react-hooks/exhaustive-deps

  const storeName = useMemo(() => {
    const map = new Map(stores.map((s) => [s.id, s.name]));
    return (id) => map.get(id) || "—";
  }, [stores]);
  const deptName = useMemo(() => {
    const map = new Map(departments.map((d) => [d.id, d.name]));
    return (id) => map.get(id) || "—";
  }, [departments]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return removals.filter((r) => {
      if (reason && r.reason !== reason) return false;
      if (term) {
        const hay =
          (r.product_name || "") +
          " " +
          (r.product_barcode || "") +
          " " +
          (r.note || "");
        if (!hay.toLowerCase().includes(term)) return false;
      }
      return true;
    });
  }, [removals, q, reason]);

  const GRID = "minmax(0,2fr) 1fr 1fr 1fr 1fr 1fr 1fr 1fr";

  return (
    <div data-testid="operator-removals-page">
      <div className="mb-4">
        <Link
          to={`/platform/companies/${cid}`}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-ink"
          data-testid="operator-removals-back"
        >
          <ArrowLeft size={14} />
          Back to tenant
        </Link>
      </div>

      <PageHeader
        eyebrow={`Tenant · ${companyName || cid}`}
        title={`Removals · last ${days}d`}
        subtitle={`${filtered.length} removal${filtered.length === 1 ? "" : "s"} in the last ${days} days matching filters.`}
      />

      <Card variant="raised" className="p-3 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative lg:col-span-1">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              value={q}
              onChange={(e) => setFilter("q", e.target.value)}
              placeholder="Product, barcode, note…"
              className="pl-9"
              data-testid="operator-removals-search"
            />
          </div>
          <Select
            value={String(days)}
            onChange={(e) => setFilter("days", e.target.value)}
            data-testid="operator-removals-filter-days"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last 365 days</option>
          </Select>
          <Select
            value={reason}
            onChange={(e) => setFilter("reason", e.target.value)}
            data-testid="operator-removals-filter-reason"
          >
            <option value="">All reasons</option>
            <option value="expired">Expired</option>
            <option value="near_expiration">Near expiration</option>
            <option value="damaged">Damaged</option>
            <option value="sold_out">Sold out</option>
            <option value="other">Other</option>
          </Select>
          <Select
            value={storeId}
            onChange={(e) => setFilter("store", e.target.value)}
            data-testid="operator-removals-filter-store"
          >
            <option value="">All stores</option>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
          <Select
            value={deptId}
            onChange={(e) => setFilter("department", e.target.value)}
            data-testid="operator-removals-filter-department"
          >
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </div>
        {(q || reason || storeId || deptId || days !== 30) && (
          <div className="mt-3 flex justify-end">
            <Button
              tier="tertiary"
              size="sm"
              onClick={() => setParams({}, { replace: true })}
              data-testid="operator-removals-clear-filters"
            >
              Reset filters
            </Button>
          </div>
        )}
      </Card>

      <OperatorTable
        gridTemplate={GRID}
        header={
          <>
            <div>Product</div>
            <div>Barcode</div>
            <div>Store</div>
            <div>Department</div>
            <div className="text-right">Qty</div>
            <div>Reason</div>
            <div>Removed by</div>
            <div>Removed at</div>
          </>
        }
        loading={loading}
        empty={!loading && filtered.length === 0}
        emptyState={
          <div className="p-10">
            <EmptyState
              icon={<Trash2 size={32} />}
              title="No removals match"
              description="Try increasing the window or clearing filters."
              data-testid="operator-removals-empty"
            />
          </div>
        }
        data-testid="operator-removals-table"
      >
        {filtered.map((r) => (
          <OperatorTableRow
            key={r.id}
            gridTemplate={GRID}
            data-testid={`operator-removal-row-${r.id}`}
          >
            <div className="min-w-0">
              <div className="font-display font-semibold truncate">
                {r.product_name || "—"}
              </div>
              {r.note && (
                <div className="font-mono text-[11px] text-muted-foreground truncate">
                  {r.note}
                </div>
              )}
            </div>
            <div className="font-mono text-xs truncate">
              {r.product_barcode || "—"}
            </div>
            <div className="font-mono text-xs truncate">
              {storeName(r.store_id)}
            </div>
            <div className="font-mono text-xs truncate">
              {deptName(r.department_id)}
            </div>
            <div className="text-right font-mono tabular-nums">
              {r.quantity ?? 0}
            </div>
            <div>
              <StatusChip tone={REASON_TONE[r.reason] || "neutral"}>
                {(r.reason || "—").replace("_", " ")}
              </StatusChip>
            </div>
            <div className="font-mono text-[11px] truncate">
              {r.removed_by_email || r.removed_by_name || "—"}
            </div>
            <div className="font-mono text-[11px] text-muted-foreground">
              {(r.removed_at || "").slice(0, 10) || "—"}
            </div>
          </OperatorTableRow>
        ))}
      </OperatorTable>
    </div>
  );
}
